import {useMemo} from 'react'

import {StockDevelopmentChartContent} from '../../../../shared/components/stockDevelopmentChart/StockDevelopmentChartContent'
import {StockDevelopmentChartSkeleton} from '../../../../shared/components/stockDevelopmentChart/StockDevelopmentChartSkeleton'
import {useMaterialStorageDataQuery, useStockDevelopmentQuery} from '../../../../shared/hooks/api'
import {useCurrentScheduleQuery} from '../../../../shared/hooks/api/schedules/useCurrentScheduleQuery'
import {getListOfDays, MomentRange} from '../../../../shared/selectors/time'
import {usePlanningChartStartEnd} from '../../../page-planning/dailyPlanning/PlanningChartStartEndProvider'
import {useSelectedOptimizerPlan} from '../../SelectedOptimizerPlanProvider'

type OptimizerStockLevelMaterialDetailChartContentProps = {
  chartRef: React.RefObject<HTMLDivElement>
  width: number
  materialId: number
}

export const OptimizerStockLevelMaterialDetailChartContent = ({
  chartRef,
  materialId,
  width
}: OptimizerStockLevelMaterialDetailChartContentProps) => {
  const {startOfPlan, endOfPlan, startOfChart, endOfChart} = usePlanningChartStartEnd()
  const planRange = [startOfPlan, endOfPlan] as MomentRange
  const chartRange = [startOfChart, endOfChart] as MomentRange

  const {selectedInTableScheduleId} = useSelectedOptimizerPlan()

  const {data: stockDevelopmentSelectedPlan, isLoading: isStockDevelopmentLoading} =
    useStockDevelopmentQuery({
      materialIds: [materialId],
      scheduleId: selectedInTableScheduleId,
      timeframe: planRange
    })

  const {data: manualSchedule} = useCurrentScheduleQuery({range: chartRange})

  const isOptimizedPlanSelected = selectedInTableScheduleId !== undefined

  const {data: stockDevelopmentProductionPlan} = useStockDevelopmentQuery({
    materialIds: isOptimizedPlanSelected && manualSchedule ? [materialId] : [],
    scheduleId: manualSchedule?.scheduleId,
    timeframe: planRange
  })
  const {data: storageData} = useMaterialStorageDataQuery({materialId})

  const listOfDays = getListOfDays({start: chartRange[0], end: chartRange[1], additionalDays: 1})

  const shouldShowChartSkeleton =
    !listOfDays || isStockDevelopmentLoading || !stockDevelopmentSelectedPlan

  const minTargetLevels = useMemo(() => {
    return (
      storageData && {
        week: storageData.minTargetLevel,
        weekend: storageData.minTargetLevelWeekend
      }
    )
  }, [storageData])

  return shouldShowChartSkeleton ? (
    <StockDevelopmentChartSkeleton />
  ) : (
    <StockDevelopmentChartContent
      listOfDays={listOfDays}
      stockData={stockDevelopmentSelectedPlan[materialId]}
      stockDataSecondary={
        isOptimizedPlanSelected && stockDevelopmentProductionPlan
          ? stockDevelopmentProductionPlan[materialId]
          : undefined
      }
      minTargetLevels={minTargetLevels}
      deadStockLevel={storageData?.deadStockLevel}
      storageCapacity={storageData?.storageCapacity}
      width={width}
      chartRef={chartRef}
    />
  )
}
